import React, { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useGame } from '../context/GameContext';
import io from 'socket.io-client';
import { 
    KEY_LEFT, KEY_RIGHT, KEY_UP, KEY_DOWN, 
    KEY_FIREFOOD, KEY_SPLIT, PLAYER_CONFIG 
} from '../utils/constants';
import {
    drawGrid, drawFood, drawVirus, drawFireFood,
    drawCells, drawBorder, drawErrorMessage, getPosition
} from '../utils/renderUtils';

const GameCanvas = () => {
    const canvasRef = useRef(null);
    const animFrameRef = useRef(null);
    const directionLockRef = useRef(false);
    const directionsRef = useRef([]);
    const reenviarRef = useRef(true);
    const playerNameRef = useRef('');
    const socketRef = useRef(null);
    const heartbeatIntervalRef = useRef(null);
    
    // Use refs for all game state to avoid stale closures in animation loop
    const gameDataRef = useRef({
        gameStart: false,
        player: { x: 0, y: 0, cells: [], hue: 0, massTotal: 0 },
        game: { width: 5000, height: 5000 },
        screen: { width: window.innerWidth, height: window.innerHeight },
        users: [],
        foods: [],
        viruses: [],
        fireFood: [],
        settings: {}
    });

    const {
        socket, setSocket, gameStart, setGameStart,
        player, setPlayer, game, setGame,
        screen, setScreen, users, setUsers,
        foods, setFoods, viruses, setViruses,
        fireFood, setFireFood, leaderboard, setLeaderboard,
        addChatMessage, settings, targetRef,
        kicked, setKicked, errorMessage, setErrorMessage,
        startPingTimeRef
    } = useGame();

    // Keep ALL data refs in sync with state - this is the key to avoiding stale closures
    useLayoutEffect(() => {
        gameDataRef.current = {
            gameStart,
            player,
            game,
            screen,
            users,
            foods,
            viruses,
            fireFood,
            settings
        };
    }, [gameStart, player, game, screen, users, foods, viruses, fireFood, settings]);

    useLayoutEffect(() => {
        socketRef.current = socket;
    }, [socket]);

    // Backup heartbeat interval - ensures heartbeats are sent reliably
    useEffect(() => {
        if (gameStart && socket) {
            // Clear any existing interval
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
            
            // Send heartbeat every 100ms as backup
            heartbeatIntervalRef.current = setInterval(() => {
                if (socketRef.current && gameDataRef.current.gameStart) {
                    socketRef.current.emit('0', targetRef.current);
                }
            }, 100);
            
            return () => {
                if (heartbeatIntervalRef.current) {
                    clearInterval(heartbeatIntervalRef.current);
                    heartbeatIntervalRef.current = null;
                }
            };
        }
    }, [gameStart, socket, targetRef]);

    // Setup socket connection
    const setupSocket = useCallback((socketInstance) => {
        socketInstance.on('pongcheck', () => {
            const latency = Date.now() - startPingTimeRef.current;
            console.log('Latency: ' + latency + 'ms');
            addChatMessage('Ping: ' + latency + 'ms');
        });

        socketInstance.on('connect_error', handleDisconnect);
        socketInstance.on('disconnect', handleDisconnect);

        socketInstance.on('welcome', (playerSettings, gameSizes) => {
            console.log('[GameCanvas] Welcome received:', playerSettings, gameSizes);
            const name = playerSettings.name || playerNameRef.current;
            
            setPlayer(prev => ({
                ...prev,
                ...playerSettings,
                name: name,
                screenWidth: screen.width,
                screenHeight: screen.height,
                target: targetRef.current
            }));
            
            const gotitData = {
                ...playerSettings,
                name: name,
                screenWidth: screen.width,
                screenHeight: screen.height,
                target: targetRef.current
            };
            socketInstance.emit('gotit', gotitData);
            
            setGame({ width: gameSizes.width, height: gameSizes.height });
            
            // Update ref immediately for animation loop
            gameDataRef.current.gameStart = true;
            
            // Send immediate heartbeat
            socketInstance.emit('0', targetRef.current);
            
            // Quick heartbeat interval while React catches up
            const quickHeartbeat = setInterval(() => {
                if (socketInstance.connected) {
                    socketInstance.emit('0', targetRef.current);
                }
            }, 200);
            
            setTimeout(() => clearInterval(quickHeartbeat), 2000);
            
            setGameStart(true);
            addChatMessage('Connected to the game!');
            canvasRef.current?.focus();
        });

        socketInstance.on('playerDied', (data) => {
            const playerName = data.playerEatenName?.length > 0 ? data.playerEatenName : 'An unnamed cell';
            addChatMessage(`{GAME} - <b>${playerName}</b> was eaten`);
        });

        socketInstance.on('playerDisconnect', (data) => {
            const name = data.name?.length > 0 ? data.name : 'An unnamed cell';
            addChatMessage(`{GAME} - <b>${name}</b> disconnected.`);
        });

        socketInstance.on('playerJoin', (data) => {
            const name = data.name?.length > 0 ? data.name : 'An unnamed cell';
            addChatMessage(`{GAME} - <b>${name}</b> joined.`);
        });

        socketInstance.on('leaderboard', (data) => {
            setLeaderboard(data.leaderboard);
        });

        socketInstance.on('serverMSG', (data) => {
            addChatMessage(data);
        });

        socketInstance.on('serverSendPlayerChat', (data) => {
            addChatMessage(`<b>${data.sender}</b>: ${data.message}`, 'friend');
        });

        socketInstance.on('serverTellPlayerMove', (playerData, userData, foodsList, massList, virusList) => {
            // Log first few updates for debugging
            if (!window._moveUpdateCount) window._moveUpdateCount = 0;
            window._moveUpdateCount++;
            if (window._moveUpdateCount <= 3) {
                console.log('[GameCanvas] serverTellPlayerMove:', { playerData, usersCount: userData?.length, foodsCount: foodsList?.length });
            }
            
            setPlayer(prev => ({
                ...prev,
                x: playerData.x,
                y: playerData.y,
                hue: playerData.hue,
                massTotal: playerData.massTotal,
                cells: playerData.cells
            }));
            setUsers(userData);
            setFoods(foodsList);
            setViruses(virusList);
            setFireFood(massList);
        });

        socketInstance.on('RIP', () => {
            console.log('[GameCanvas] Player died');
            setGameStart(false);
            gameDataRef.current.gameStart = false;
            const canvas = canvasRef.current;
            if (canvas) {
                const ctx = canvas.getContext('2d');
                drawErrorMessage('You died!', ctx, screen);
            }
        });

        socketInstance.on('kick', (reason) => {
            console.log('[GameCanvas] Player kicked:', reason);
            setGameStart(false);
            gameDataRef.current.gameStart = false;
            setKicked(true);
            const canvas = canvasRef.current;
            if (canvas) {
                const ctx = canvas.getContext('2d');
                const msg = reason ? `Kicked: ${reason}` : 'You were kicked!';
                drawErrorMessage(msg, ctx, screen);
            }
            socketInstance.close();
        });
    }, [screen, setPlayer, setGame, setGameStart, setUsers, setFoods, setViruses, setFireFood, setLeaderboard, addChatMessage, targetRef, startPingTimeRef, setKicked]);

    const handleDisconnect = () => {
        if (socket) {
            socket.close();
            if (!kicked) {
                const canvas = canvasRef.current;
                if (canvas) {
                    const ctx = canvas.getContext('2d');
                    drawErrorMessage('Disconnected!', ctx, screen);
                }
            }
        }
    };

    // Handle start game event
    useEffect(() => {
        const handleStartGame = (e) => {
            const { playerName, type, ftAccountId } = e.detail;
            
            // Store in ref so it's available in socket events
            playerNameRef.current = playerName;
            setPlayer(prev => ({ ...prev, name: playerName }));
            
            if (!socket) {
                const socketOptions = {
                    query: { type, name: playerName },
                    transports: ['websocket', 'polling'],
                };
                
                const newSocket = io(socketOptions);
                
                // Update ref immediately
                socketRef.current = newSocket;
                
                setSocket(newSocket);
                setupSocket(newSocket);
                
                newSocket.on('connect', () => {
                    newSocket.emit('respawn', { 
                        name: playerName,
                        ftAccountId: ftAccountId 
                    });
                });
                
                newSocket.on('connect_error', (err) => {
                    console.error('Socket connection error:', err.message);
                });
                
                newSocket.io.engine.on('upgrade', () => {});
            } else {
                socket.emit('respawn', { 
                    name: playerName,
                    ftAccountId: ftAccountId 
                });
            }
        };

        window.addEventListener('startGame', handleStartGame);
        return () => window.removeEventListener('startGame', handleStartGame);
    }, [socket, setSocket, setPlayer, setupSocket]);

    // Canvas input handling
    const updateTarget = useCallback((directions) => {
        targetRef.current = { x: 0, y: 0 };
        let directionHorizontal = 0;
        let directionVertical = 0;

        for (let dir of directions) {
            if (directionHorizontal === 0) {
                if (dir === KEY_LEFT) directionHorizontal -= Number.MAX_VALUE;
                else if (dir === KEY_RIGHT) directionHorizontal += Number.MAX_VALUE;
            }
            if (directionVertical === 0) {
                if (dir === KEY_UP) directionVertical -= Number.MAX_VALUE;
                else if (dir === KEY_DOWN) directionVertical += Number.MAX_VALUE;
            }
        }

        targetRef.current.x += directionHorizontal;
        targetRef.current.y += directionVertical;
    }, [targetRef]);

    const handleMouseMove = useCallback((e) => {
        if (!directionLockRef.current && canvasRef.current) {
            const rect = canvasRef.current.getBoundingClientRect();
            targetRef.current.x = e.clientX - rect.width / 2;
            targetRef.current.y = e.clientY - rect.height / 2;
        }
    }, [targetRef]);

    const handleMouseOut = useCallback(() => {
        if (!settings.continuity) {
            targetRef.current = { x: 0, y: 0 };
        }
    }, [settings.continuity, targetRef]);

    const handleTouchMove = useCallback((e) => {
        e.preventDefault();
        if (!directionLockRef.current && canvasRef.current && e.touches.length > 0) {
            const rect = canvasRef.current.getBoundingClientRect();
            targetRef.current.x = e.touches[0].clientX - rect.width / 2;
            targetRef.current.y = e.touches[0].clientY - rect.height / 2;
        }
    }, [targetRef]);

    const handleKeyDown = useCallback((e) => {
        const key = e.which || e.keyCode;
        const directional = [KEY_LEFT, KEY_RIGHT, KEY_UP, KEY_DOWN].includes(key);
        
        if (directional) {
            directionLockRef.current = true;
            if (!directionsRef.current.includes(key)) {
                directionsRef.current.push(key);
                updateTarget(directionsRef.current);
                if (socket) socket.emit('0', targetRef.current);
            }
        }
    }, [socket, updateTarget, targetRef]);

    const handleKeyUp = useCallback((e) => {
        const key = e.which || e.keyCode;
        reenviarRef.current = true;
        
        const directional = [KEY_LEFT, KEY_RIGHT, KEY_UP, KEY_DOWN].includes(key);
        if (directional) {
            const index = directionsRef.current.indexOf(key);
            if (index > -1) {
                directionsRef.current.splice(index, 1);
                updateTarget(directionsRef.current);
                if (directionsRef.current.length === 0) directionLockRef.current = false;
                if (socket) socket.emit('0', targetRef.current);
            }
        }
    }, [socket, updateTarget, targetRef]);

    const handleKeyPress = useCallback((e) => {
        const key = e.which || e.keyCode;
        
        if (key === KEY_FIREFOOD && reenviarRef.current && socket) {
            socket.emit('1');
            reenviarRef.current = false;
        } else if (key === KEY_SPLIT && reenviarRef.current && socket) {
            const audio = document.getElementById('split_cell');
            if (audio) audio.play();
            socket.emit('2');
            reenviarRef.current = false;
        }
    }, [socket]);

    // Main render function - reads from refs to always have latest data
    const render = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            // Don't log error on every frame - just return silently
            return;
        }
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        
        // Get latest data from refs
        const data = gameDataRef.current;
        const { gameStart: isStarted, player: p, game: g, screen: s, users: u, foods: f, viruses: v, fireFood: ff, settings: set } = data;
        
        // Ensure canvas has valid dimensions
        const targetWidth = s.width || window.innerWidth || 800;
        const targetHeight = s.height || window.innerHeight || 600;
        
        if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            console.log('[Render] Canvas size updated to:', canvas.width, 'x', canvas.height);
        }
        
        // Clear and fill with background
        ctx.fillStyle = set.backgroundColor || '#f2fbff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        if (!isStarted) {
            // Draw waiting screen
            ctx.fillStyle = '#3498db';
            ctx.fillRect(50, 50, 300, 150);
            
            ctx.fillStyle = '#fff';
            ctx.font = 'bold 20px Arial';
            ctx.fillText('GAME CANVAS ACTIVE', 80, 100);
            ctx.font = '16px Arial';
            ctx.fillText('Waiting for game to start...', 80, 130);
            ctx.fillText(`Canvas: ${canvas.width}x${canvas.height}`, 80, 160);
            return;
        }

        // Draw grid
        drawGrid(set, p, s, ctx);

        // Draw food
        f.forEach(food => {
            const position = getPosition(food, p, s);
            drawFood(position, food, ctx);
        });

        // Draw fire food (mass projectiles)
        ff.forEach(mass => {
            const position = getPosition(mass, p, s);
            drawFireFood(position, mass, PLAYER_CONFIG, ctx);
        });

        // Draw viruses
        v.forEach(virus => {
            const position = getPosition(virus, p, s);
            drawVirus(position, virus, ctx);
        });

        // Calculate borders
        const borders = {
            left: s.width / 2 - p.x,
            right: s.width / 2 + g.width - p.x,
            top: s.height / 2 - p.y,
            bottom: s.height / 2 + g.height - p.y
        };

        if (set.borderDraw) {
            drawBorder(borders, ctx);
        }

        // Prepare cells for drawing
        const cellsToDraw = [];
        for (let user of u) {
            const color = `hsl(${user.hue}, 100%, 50%)`;
            const borderColor = `hsl(${user.hue}, 100%, 45%)`;
            for (let cell of user.cells) {
                cellsToDraw.push({
                    color,
                    borderColor,
                    mass: cell.mass,
                    name: user.name,
                    radius: cell.radius,
                    x: cell.x - p.x + s.width / 2,
                    y: cell.y - p.y + s.height / 2,
                    isFrozen: user.isFrozen || false,
                    cashOutTimeRemaining: user.cashOutTimeRemaining || 0
                });
            }
        }

        cellsToDraw.sort((a, b) => a.mass - b.mass);
        drawCells(cellsToDraw, PLAYER_CONFIG, set.toggleMassState, borders, ctx);
    }, []); // No dependencies - uses refs

    // Animation loop - runs continuously using refs to avoid stale closures
    useEffect(() => {
        console.log('[GameCanvas] Component mounted, starting animation loop');
        let isRunning = true;
        
        const loop = () => {
            if (!isRunning) return;
            
            // Schedule next frame
            animFrameRef.current = requestAnimationFrame(loop);
            
            // Send heartbeat if game is active
            if (gameDataRef.current.gameStart && socketRef.current) {
                socketRef.current.emit('0', targetRef.current);
            }
            
            // Render frame
            render();
        };
        
        // Start the loop
        loop();
        
        return () => {
            console.log('[GameCanvas] Component unmounting, stopping animation loop');
            isRunning = false;
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current);
                animFrameRef.current = null;
            }
        };
    }, [render, targetRef]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const newWidth = window.innerWidth;
            const newHeight = window.innerHeight;
            
            setScreen({ width: newWidth, height: newHeight });
            setPlayer(prev => ({
                ...prev,
                screenWidth: newWidth,
                screenHeight: newHeight
            }));

            if (socket && canvasRef.current) {
                canvasRef.current.width = newWidth;
                canvasRef.current.height = newHeight;
                socket.emit('windowResized', { 
                    screenWidth: newWidth, 
                    screenHeight: newHeight 
                });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [socket, setScreen, setPlayer]);

    // Initialize canvas size - run on mount and when screen changes
    useEffect(() => {
        if (canvasRef.current) {
            const width = screen.width || window.innerWidth || 800;
            const height = screen.height || window.innerHeight || 600;
            canvasRef.current.width = width;
            canvasRef.current.height = height;
            console.log('[GameCanvas] Canvas initialized with dimensions:', width, 'x', height);
            
            // Also draw initial frame to confirm canvas is working
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#f2fbff';
                ctx.fillRect(0, 0, width, height);
                ctx.fillStyle = '#2ecc71';
                ctx.fillRect(width/2 - 100, height/2 - 50, 200, 100);
                ctx.fillStyle = '#fff';
                ctx.font = 'bold 18px Arial';
                ctx.textAlign = 'center';
                ctx.fillText('Canvas Ready!', width/2, height/2 + 8);
                ctx.textAlign = 'left';
            }
        }
    }, [screen]);

    // Cleanup socket on unmount
    useEffect(() => {
        return () => {
            if (socket) {
                socket.close();
            }
        };
    }, [socket]);

    // Debug: Log canvas render state
    useEffect(() => {
        console.log('[GameCanvas] Render state:', {
            canvasRef: !!canvasRef.current,
            screenWidth: screen.width,
            screenHeight: screen.height,
            gameStart,
            socket: !!socket
        });
    }, [screen.width, screen.height, gameStart, socket]);

    return (
        <canvas
            ref={canvasRef}
            id="cvs"
            width={screen.width || window.innerWidth}
            height={screen.height || window.innerHeight}
            tabIndex="1"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: '#f2fbff',
                display: 'block',
                zIndex: 1,
                visibility: 'visible',
                opacity: 1
            }}
            onMouseMove={handleMouseMove}
            onMouseOut={handleMouseOut}
            onTouchStart={handleTouchMove}
            onTouchMove={handleTouchMove}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onKeyPress={handleKeyPress}
        />
    );
};

export default GameCanvas;


